import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { saveAs } from 'file-saver';
import { from } from 'rxjs';
import {
  CompanyComplianceType,
  ComplianceSetting,
  User
} from 'src/app/classes/models';
import { Company } from 'src/app/classes/models';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { DateRangeSelectionModalComponent } from 'src/app/modals/date-range-selection-modal/date-range-selection-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyService } from 'src/app/services/company.service';
import { ReportService } from 'src/app/services/report.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-client-reports',
  templateUrl: './client-reports.component.html',
  styleUrl: './client-reports.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientReportsComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private ToastService: ToastService,
    private ReportService: ReportService,
    private AuthService: AuthService,
    private ModalService: NgbModal,
    private CompanyService: CompanyService,
    private UtilityService: UtilityService
  ) {
    const CompanyId = this.AuthService.getUserCompanyId();
    this.CompanyService.getCompany(
      CompanyId,
      this.UtilityService.stringifyObjectsInQueryObject({
        attributes: ['id', 'hasGlobalCompliance'],
        ComplianceSetting: {
          attributes: ['riskLevel', 'sampleCert'],
          CompanyComplianceType: {}
        },
        CompanyComplianceType: {
          ComplianceSetting: {}
        }
      })
    ).subscribe({
      next: (Company) => {
        this.Company = Company;
        const { ComplianceSettings, hasGlobalCompliance } = this.Company;

        if (ComplianceSettings && ComplianceSettings.length > 0) {
          this.hasRiskLevelHigherThanOne = ComplianceSettings.some(
            (cs) => cs.riskLevel && cs.riskLevel > 1
          );

          this.SortedComplianceSettings = ComplianceSettings.filter(
            (cs) =>
              (hasGlobalCompliance ? cs.riskLevel === 0 : cs.riskLevel !== 0) &&
              cs.sampleCert !== null
          ).sort((a, b) => {
            const typeA =
              a.CompanyComplianceType && a.CompanyComplianceType.type
                ? a.CompanyComplianceType.type
                : '';
            const typeB =
              b.CompanyComplianceType && b.CompanyComplianceType.type
                ? b.CompanyComplianceType.type
                : '';
            const typeCompare = typeA.localeCompare(typeB);

            if (typeCompare !== 0) {
              return typeCompare;
            }

            const riskA = typeof a.riskLevel === 'number' ? a.riskLevel : 0;
            const riskB = typeof b.riskLevel === 'number' ? b.riskLevel : 0;

            return riskA - riskB;
          });
        }
        this._cdr.markForCheck();
      }
    });
  }

  hasRiskLevelHigherThanOne: boolean = false;
  Company?: Company;

  RiskLevelMap: Map<number, string> = new Map([
    [1, 'Basic'],
    [2, 'Comprehensive'],
    [3, 'High']
  ]);
  SortedComplianceSettings?: ComplianceSetting[];

  LoggedInUser!: User;

  openPDFNewTab(url: string) {
    window.open(url, '_blank');
  }

  // Spotlight and preferred mean the same thing
  generateSpotlightVendorsReport() {
    this.ReportService.generateSpotlightVendorsReport().subscribe({
      next: (Report) => {
        saveAs(new Blob([Report]), 'SpotlightVendors.xlsx');
      }
    });
  }

  generateSiteAssignedVendorsReport() {
    this.ReportService.generateSiteAssignedVendorsReport().subscribe({
      next: (Report) => {
        saveAs(new Blob([Report]), 'SiteAssignedVendors.xlsx');
      }
    });
  }

  generateSuspendedVendorsReport() {
    const { result } = this.ModalService.open(
      DateRangeSelectionModalComponent,
      { backdrop: 'static', size: 'md' }
    );
    from(result).subscribe({
      next: ({ startDate, endDate }) => {
        this.ReportService.generateSuspendedVendorsReport({
          startDate,
          endDate
        }).subscribe({
          next: (Report) => {
            saveAs(new Blob([Report]), 'SuspendedVendors.xlsx');
          }
        });
      }
    });
  }

  generateComplianceSummaryReport() {
    this.ReportService.generateComplianceSummaryReport().subscribe({
      next: (Report) => {
        saveAs(new Blob([Report]), 'ComplianceSummary.xlsx');
      }
    });
  }

  generateBusinessVerifiedComplianceReport() {
    this.ReportService.generateBusinessVerifiedComplianceReport().subscribe({
      next: (Report) => {
        saveAs(new Blob([Report]), 'BusinessVerifiedCompliance.xlsx');
      }
    });
  }

  generateComplianceDetailReport() {
    this.ReportService.generateComplianceDetailReport().subscribe({
      next: (Report) => {
        saveAs(new Blob([Report]), 'ComplianceDetail.xlsx');
      }
    });
  }

  generateTieredTradesReport() {
    this.ReportService.generateTieredTradesReport().subscribe({
      next: (Report) => {
        saveAs(new Blob([Report as ArrayBuffer]), 'TieredTrades.xlsx');
      }
    });
  }

  generateFeeVouchersReport() {
    const { result } = this.ModalService.open(
      DateRangeSelectionModalComponent,
      { backdrop: 'static', size: 'md' }
    );
    from(result).subscribe({
      next: ({ startDate, endDate }) => {
        this.ReportService.generateFeeVouchersReport({
          startDate,
          endDate
        }).subscribe({
          next: (Report) => {
            saveAs(new Blob([Report]), 'FeeVouchers.xlsx');
          }
        });
      }
    });
  }

  generateTradesAndTradeCategoriesReport() {
    this.ReportService.generateTradesAndTradeCategoriesReport().subscribe({
      next: (Report) => {
        saveAs(new Blob([Report as ArrayBuffer]), 'TradesAndCategories.xlsx');
      }
    });
  }

  generateInsuranceTypeAndDatesReport() {
    this.ReportService.generateInsuranceTypeAndDatesReport().subscribe({
      next: (Report) => {
        saveAs(new Blob([Report]), 'InsuranceTypeAndDates.xlsx');
      }
    });
  }

  generateVendorsInReviewReport() {
    this.ReportService.generateVendorsInReviewReport().subscribe({
      next: (Report) => {
        saveAs(new Blob([Report as ArrayBuffer]), 'VendorsInReviewStatus.xlsx');
      }
    });
  }

  generateAPInsuranceTypeAndDatesReport() {
    this.ReportService.generateAPInsuranceTypeAndDatesReport().subscribe({
      next: (Report) => {
        saveAs(new Blob([Report]), 'APInsuranceTypeAndDates.xlsx');
      }
    });
  }

  generateLinkedVendorsReport() {
    this.ReportService.generateLinkedVendorsReport().subscribe({
      next: (Report) => {
        saveAs(new Blob([Report]), 'LinkedVendors.xlsx');
      }
    });
  }

  generateVendorsRegisteredReport() {
    const { result } = this.ModalService.open(
      DateRangeSelectionModalComponent,
      { backdrop: 'static', size: 'md' }
    );
    from(result).subscribe({
      next: ({ startDate, endDate }) => {
        this.ReportService.generateVendorsRegisteredReport({
          startDate,
          endDate
        }).subscribe({
          next: (Report) => {
            saveAs(new Blob([Report as ArrayBuffer]), 'VendorsRegistered.xlsx');
          }
        });
      }
    });
  }

  generateVendorPaymentDetailReport() {
    const { result } = this.ModalService.open(
      DateRangeSelectionModalComponent,
      { backdrop: 'static', size: 'md' }
    );
    from(result).subscribe({
      next: ({ startDate, endDate }) => {
        this.ReportService.generateVendorPaymentDetailReport({
          startDate,
          endDate
        }).subscribe({
          next: (Report) => {
            saveAs(new Blob([Report]), 'VendorPaymentDetail.xlsx');
          }
        });
      }
    });
  }

  generateVendorStatusesReport() {
    const { result } = this.ModalService.open(
      DateRangeSelectionModalComponent,
      { backdrop: 'static', size: 'md' }
    );
    from(result).subscribe({
      next: ({ startDate, endDate }) => {
        this.ReportService.generateVendorStatusesReport({
          startDate,
          endDate
        }).subscribe({
          next: (Report) => {
            saveAs(
              new Blob([Report as ArrayBuffer]),
              'VendorStatusesReport.xlsx'
            );
          }
        });
      }
    });
  }

  generateVendorPaymentSummaryReport() {
    const { result } = this.ModalService.open(
      DateRangeSelectionModalComponent,
      { backdrop: 'static', size: 'md' }
    );
    from(result).subscribe({
      next: ({ startDate, endDate }) => {
        this.ReportService.generateVendorPaymentSummaryReport({
          startDate,
          endDate
        }).subscribe({
          next: (Report) => {
            saveAs(new Blob([Report]), 'VendorPaymentSummary.xlsx');
          }
        });
      }
    });
  }

  generateVendorTradesReport() {
    this.ReportService.generateVendorTradesReport().subscribe({
      next: (Report) => {
        saveAs(new Blob([Report as ArrayBuffer]), 'VendorTradesReport.xlsx');
      }
    });
  }

  generateBusinessVerifiedVendorInvoicesReport() {
    // Open date range picker modal.
    const { result } = this.ModalService.open(
      DateRangeSelectionModalComponent,
      { backdrop: 'static', size: 'md' }
    );
    from(result).subscribe({
      next: ({ startDate, endDate }) => {
        this.ReportService.generateBusinessVerifiedInvoicesReport({
          startDate,
          endDate
        }).subscribe({
          next: (Report) => {
            saveAs(new Blob([Report]), 'BusinessVerifiedInvoices.xlsx');
          }
        });
      }
    });
  }

  generateBusinessVerificationOverrideReport() {
    const { result } = this.ModalService.open(
      DateRangeSelectionModalComponent,
      { backdrop: 'static', size: 'md' }
    );
    from(result).subscribe({
      next: ({ startDate, endDate }) => {
        this.ReportService.generateBusinessVerificationOverrideReport({
          startDate,
          endDate
        }).subscribe({
          next: (Report) => {
            saveAs(new Blob([Report]), 'BusinessVerificationOverride.xlsx');
          }
        });
      }
    });
  }

  generateSiteAssignmentsForAPReport() {
    this.ReportService.generateSiteAssignmentsForAPReport().subscribe({
      next: (Report) => {
        saveAs(new Blob([Report]), 'SiteAssignmentsForAccountsPayable.xlsx');
      }
    });
  }

  generateVendorsExpiredInsuranceReport() {
    const { result } = this.ModalService.open(
      DateRangeSelectionModalComponent,
      { backdrop: 'static', size: 'md' }
    );
    from(result).subscribe({
      next: ({ startDate, endDate }) => {
        this.ReportService.generateVendorsExpiredInsuranceReport({
          startDate,
          endDate
        }).subscribe({
          next: (Report) => {
            saveAs(
              new Blob([Report as ArrayBuffer]),
              'VendorsExpiredInsuranceReport.xlsx'
            );
          }
        });
      }
    });
  }

  generateNewlyApprovedVendorsReport() {
    const { result } = this.ModalService.open(
      DateRangeSelectionModalComponent,
      { backdrop: 'static', size: 'md' }
    );
    from(result).subscribe({
      next: ({ startDate, endDate }) => {
        this.ReportService.generateNewlyApprovedVendorsReport({
          startDate,
          endDate
        }).subscribe({
          next: (Report) => {
            saveAs(
              new Blob([Report as ArrayBuffer]),
              'NewlyApprovedVendorsReport.xlsx'
            );
          }
        });
      }
    });
  }

  userHasSpecificAccountingSystem(apSystemName: string) {
    return !!this.LoggedInUser?.Company?.ApConfigs?.find((ApConfig) => {
      const { apSystem } = ApConfig;
      return apSystem === apSystemName;
    });
  }

  ngOnInit(): void {
    this.LoggedInUser = this.AuthService.getUser();
    this._cdr.markForCheck();
  }
}
